import { useQuery } from 'react-query';
import { AxiosError } from 'axios';

import { client } from '@/util/client';
import { QuestionnaireVariant } from '@aldebaran/prisma';

/**
 * Reads the launch context for an in-office assisted questionnaire. Authorized
 * by the RESTRICTED patient cookie that the pro app's
 * `POST /consultation/:id/launch-assisted-questionnaire` call already set in the
 * shared-domain cookie jar. Returns the consultation so the patient app can
 * hydrate professional / practice / variant / patient.
 */
export interface AssistedQuestionnaireLaunchData {
  id: string;
  date: string | null;
  variant: QuestionnaireVariant | null;
  noAppointment: boolean;
  questionnaireId: string | null;
  patient: { id: string };
  /** The consultation's own practice — always present; used to hydrate the
   * practice when no professional is assigned. */
  practice: { slug: string };
  /** Null when the consultation has no assigned professional. */
  professional: {
    slug: string;
    defaultQuestionnaireVariant: QuestionnaireVariant | null;
    practice: { slug: string };
  } | null;
}

export const useAssistedQuestionnaireLaunch = (consultationId: string) =>
  useQuery<AssistedQuestionnaireLaunchData, AxiosError>(
    ['assisted-questionnaire-launch', consultationId],
    async () =>
      client(
        `/consultation/${consultationId}/initialize-assisted-questionnaire`,
      ).then((response) => response?.data),
    { enabled: !!consultationId, retry: false },
  );
